<script setup lang="ts">
    withDefaults(
        defineProps<{
            required?: boolean;
            disabled?: boolean;
            invalid?: boolean;
        }>(),
        {
            required: false,
            disabled: false,
            invalid: false,
        },
    );

    /*
      Stop a double click on the label text from selecting the text around it, as Reka's Label did.
      Clicks on a control inside the label keep their default behaviour.
    */
    function onMousedown(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (target.closest('button, input, select, textarea')) return;
        if (event.detail > 1) event.preventDefault();
    }
</script>

<template>
    <label
        data-slot="label"
        :class="[
            'ui-label',
            {
                'ui-label--required': required,
                'ui-label--disabled': disabled,
                'ui-label--invalid': invalid,
            },
        ]"
        @mousedown="onMousedown"
    >
        <slot />
    </label>
</template>

<style scoped>
    /*
      Below `utilities` so consumers' Tailwind classes still override these defaults, above Bulma's `base`.
      Font size and text color are inherited.
    */
    @layer components {
        .ui-label {
            display: flex;
            align-items: center;
            gap: 8px;
            line-height: 1;
            font-weight: var(--font-weight-semibold);
            user-select: none;
        }

        /* A label that wraps a disabled control is disabled too. */
        .ui-label--disabled,
        .ui-label:has(:disabled) {
            color: var(--color-content-subdued);
            cursor: not-allowed;
        }

        .ui-label--invalid {
            color: var(--color-content-negative);
        }

        .ui-label--required::after {
            content: '*';
            margin-inline-start: -4px;
            color: var(--color-content-informative);
        }

        .ui-label--required:is(.ui-label--invalid)::after {
            color: var(--color-content-negative);
        }

        .ui-label--required:is(.ui-label--disabled, :has(:disabled))::after {
            color: var(--color-content-subdued);
        }
    }
</style>
